/*
 * Copyright 2026 Sophos Limited. All rights reserved.
 *
 * 'Sophos' and 'Sophos Anti-Virus' are registered trademarks of Sophos Limited and Sophos Group. All other product
 * and company names mentioned are trademarks or registered trademarks of their respective owners.
 */
import { Box, Button, Card, CardContent, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState, type ReactElement } from "react";
import { LoadingButton } from "../../components/LoadingButton/LoadingButton";
import { formatTeamsMessage } from "../../helpers/formatTeamsMessage";
import { dailyChecksDataAtom, loadableFetchDailyChecksState } from "../../services/dailyChecksApi";
import { DLQTable } from "./DLQTable";
import { PipelineErrorsTable } from "./PipelineErrorsTable";
import { CloudSourcesTable } from "./CloudSourcesTable";
import { FilterStatsTable } from "./FilterStatsTable";

/**
 * Daily Checks view — displays a production health summary across DLQs,
 * pipeline errors, cloud sources, and filter rate-limit stats.
 */
export function DailyChecksView(): ReactElement {
  const data = useAtomValue(dailyChecksDataAtom);
  const [fetchState, fetch] = useAtom(loadableFetchDailyChecksState);
  const [copied, setCopied] = useState(false);

  const isLoading = fetchState.state === "loading" || fetchState.state === "initial";

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleCopyForTeams = () => {
    if (!data) return;
    navigator.clipboard.writeText(formatTeamsMessage(data));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Container maxWidth="md">
      <Grid container direction="column" gap={4}>
        <Grid item xs={1}>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h4" gutterBottom>
                Daily Checks
              </Typography>
              {data?.checkedAt && (
                <Typography variant="caption" color="text.secondary">
                  {`Last checked: ${new Date(data.checkedAt).toLocaleString()}`}
                </Typography>
              )}
            </Box>
            <Box display="flex" flexDirection="row" gap={1} sx={{ mt: 1 }}>
              <Button variant="outlined" size="small" onClick={handleCopyForTeams} disabled={!data}>
                {copied ? "Copied!" : "Copy for Teams"}
              </Button>
              <LoadingButton variant="contained" size="small" state={fetchState.state} onClick={() => fetch()}>
                Refresh
              </LoadingButton>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={1}>
          <Card component="section">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dead Letter Queues
              </Typography>
              {isLoading && <CircularProgress size="1.5rem" />}
              {!isLoading && !data?.dlq && <Typography color="text.secondary">Unable to load DLQ data.</Typography>}
              {!isLoading && data?.dlq && data.dlq.items.length === 0 && (
                <Typography color="text.secondary">No items on any DLQ.</Typography>
              )}
              {!isLoading && data?.dlq && data.dlq.items.length > 0 && <DLQTable data={data.dlq} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={1}>
          <Card component="section">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pipeline Errors (last 24h)
              </Typography>
              {isLoading && <CircularProgress size="1.5rem" />}
              {!isLoading && !data?.errors && (
                <Typography color="text.secondary">Unable to load pipeline error data.</Typography>
              )}
              {!isLoading && data?.errors && data.errors.errors.length === 0 && (
                <Typography color="text.secondary">No pipeline errors in the last 24h.</Typography>
              )}
              {!isLoading && data?.errors && data.errors.errors.length > 0 && (
                <PipelineErrorsTable data={data.errors} />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={1}>
          <Card component="section">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cloud Sources
              </Typography>
              {isLoading && <CircularProgress size="1.5rem" />}
              {!isLoading && !data?.cloudSources && (
                <Typography color="text.secondary">Unable to load cloud source data.</Typography>
              )}
              {!isLoading && data?.cloudSources && data.cloudSources.sources.length === 0 && (
                <Typography color="text.secondary">No cloud source data available.</Typography>
              )}
              {!isLoading && data?.cloudSources && data.cloudSources.sources.length > 0 && (
                <CloudSourcesTable data={data.cloudSources} />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={1}>
          <Card component="section">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filter Rate Limit — Top Organisations
              </Typography>
              {isLoading && <CircularProgress size="1.5rem" />}
              {!isLoading && !data?.filterStats && (
                <Typography color="text.secondary">Unable to load filter stats data.</Typography>
              )}
              {!isLoading && data?.filterStats && data.filterStats.topOrgs.length === 0 && (
                <Typography color="text.secondary">No filter rate limit activity.</Typography>
              )}
              {!isLoading && data?.filterStats && data.filterStats.topOrgs.length > 0 && (
                <FilterStatsTable data={data.filterStats} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
